import React, { useEffect, Fragment } from 'react';
import proptypes from 'prop-types';
const { arrayOf, bool, string } = proptypes;
import {
  Alert,
  Link,
  Skeleton,
  SkeletonBlock,
  SkeletonLine,
  Typography
} from '@one-thd/sui-atomic-components';
import { CompatibleAccessories } from '@thd-olt-component-react/compatible-accessories';
import { usePartsAndServices } from '../PartsAndServicesProvider';
import { HaulAwayCheckboxes } from './HaulAwayCheckboxes';
import { InstallationRadioBtns } from './InstallationRadioBtns';
import { Pod } from './Pod';
import {
  INFO_ALERT_SERVICES,
  INFO_ALERT_BOPIS,
  INSTALL_REQUIREMENTS_URL,
  INSTALL_TITLE,
  INSTALL_EMPTY,
  HAULAWAY_EMPTY,
  ADD_ON_CATEGORIES,
  ADD_ON_TYPES,
  PARTS_AND_SERVICES_DOWN_ALERT_MSG
} from '../../constants';
import { installationOptionsData } from '../../staticData';
import { capitalize, getInstallationDetails, filterByCategoryAndType, ctaAnalytics } from '../../utils';

export const Services = ({ exclusionListServices, hideTitle, showAlert }) => {
  const {
    disableHeader,
    fulfillmentSelections,
    productsMap,
    showLoadingSkeleton,
    enableInstallationFirst,
    isPSPPServiceDown,
    newProductSelections,
    handleProductSelection,
    newItemsArray,
    newAddOnsArrays,
    newProductsArray,
    currentPage,
    isEnableCompatibleAccessoriesPAS,
    typeOfSelection
  } = usePartsAndServices();

  // Helper function to check if a product is BOPIS
  const isProductBopis = (lineItemId) => fulfillmentSelections[lineItemId] === 'BOPIS';

  // Determine if ANY product is BOPIS using fulfillmentSelections
  const hasAnyBopisProduct = [...productsMap.entries()].some(([lineItemId]) => isProductBopis(lineItemId));

  useEffect(() => {
    ctaAnalytics({
      newItemsArray,
      newAddOnsArrays,
      newProductsArray,
      currentPage,
      isEnableCompatibleAccessoriesPAS,
      typeOfSelection,
      displayed: true
    });

    // TODO remove once iframe is gone
    if (!disableHeader) {
      window.scrollTo({ top: 0 });
    }
  }, []);

  const formattedTitle = capitalize(
    enableInstallationFirst ? INSTALL_TITLE : `Next, ${INSTALL_TITLE}`
  );

  return (
    <div data-testid="services-page">
      {showAlert && !hasAnyBopisProduct && (
        <Alert status="info">
          <Typography>{INFO_ALERT_SERVICES}</Typography>
        </Alert>
      )}
      {!hideTitle && (
        <div className="sui-mb-4">
          <Typography variant="h3">{formattedTitle}</Typography>
        </div>
      )}
      {!hasAnyBopisProduct && (
        <>
          <article data-testid="installation-options">
            {installationOptionsData.map((option) => (
              <div className="sui-flex sui-mb-4" key={option.title}>
                <div className="sui-mt-1 sui-mr-3"> {option.icon} </div>
                <div>
                  <Typography variant="h5" height="normal"> {option.title} </Typography>
                  <Typography variant="body-base"> {option.description} </Typography>
                </div>
              </div>
            ))}
            <Link href={INSTALL_REQUIREMENTS_URL} target="_blank">Installation Requirements</Link>
          </article>
          <hr className="sui-my-4" />
        </>
      )}
      {showLoadingSkeleton && (
        <div className="sui-mt-9 sui-mb-8">
          <Skeleton grow disablePadding>
            <SkeletonBlock />
            <SkeletonLine numberOfLines={5} />
          </Skeleton>
        </div>
      )}
      {!showLoadingSkeleton && !isPSPPServiceDown && (
        <div className="sui-pb-1">
          {[...productsMap.entries()]
            .filter(([lineItemId]) => !exclusionListServices.includes(lineItemId))
            .map(([lineItemId, product], productIndex) => {
              const { addOns = [] } = product || {};
              // Determine if this specific product is BOPIS using fulfillmentSelections
              const isCurrentProductBopis = isProductBopis(lineItemId);
              const { hasInstallation, hasHookup, hasTwoStepInstallation } = getInstallationDetails({ addOns });
              const requiredItems = filterByCategoryAndType({
                addOns,
                lineItemId,
                addOnCategories: ADD_ON_CATEGORIES.INSTALLATION
              });
              const haulAwayItems = filterByCategoryAndType({
                addOns,
                lineItemId,
                addOnCategories: ADD_ON_CATEGORIES.HAUL_AWAY,
                addOnTypes: ADD_ON_TYPES.HAUL_AWAY
              });
              const checkedMatch = newProductSelections[lineItemId]
                ? newProductSelections[lineItemId].map((selectedProduct) => selectedProduct.itemId)
                : [];
              return (
                <Fragment key={lineItemId}>
                  <div>
                    {!isCurrentProductBopis && !haulAwayItems.length && (
                      <Alert status="warning">{HAULAWAY_EMPTY}</Alert>
                    )}
                    <Pod lineItemId={lineItemId} product={product} />
                    {isCurrentProductBopis && (
                      <div className="sui-mt-4">
                        <Alert status="info">
                          <Typography>{INFO_ALERT_BOPIS}</Typography>
                        </Alert>
                      </div>
                    )}
                    {!isCurrentProductBopis && (
                      <div className="sui-mt-4">
                        {hasInstallation || hasHookup ? (
                          <InstallationRadioBtns
                            lineItemId={lineItemId}
                            requiredItems={requiredItems}
                            installationOptions={{ hasInstallation, hasHookup, hasTwoStepInstallation }}
                          />
                        ) : (<Alert status="warning">{INSTALL_EMPTY}</Alert>)}
                      </div>
                    )}
                    {!isCurrentProductBopis && haulAwayItems.length !== 0 && (
                      <div className="sui-mt-3 sui-mb-2">
                        <HaulAwayCheckboxes lineItemId={lineItemId} haulAwayItems={haulAwayItems} />
                      </div>
                    )}
                    <CompatibleAccessories
                      parent="PAS"
                      matchIdsForCheckbox={checkedMatch}
                      itemId={product.itemId}
                      handleChecked={handleProductSelection}
                      lineItemId={lineItemId}
                    />
                  </div>
                  {productsMap.size > 1 && productIndex < productsMap.size - 1 && (<hr className="sui-my-4" />)}
                </Fragment>
              );
            }
            )}
        </div>
      )}
      {isPSPPServiceDown && (
        <div className="sui-mt-4">
          <Alert status="error">
            <Typography>{PARTS_AND_SERVICES_DOWN_ALERT_MSG}</Typography>
          </Alert>
        </div>
      )}
    </div>
  );
};

Services.propTypes = {
  exclusionListServices: arrayOf(string).isRequired,
  hideTitle: bool,
  showAlert: bool
};

Services.defaultProps = {
  hideTitle: false,
  showAlert: false
};

Services.displayName = 'Services';